// @greenlight-spec: 12-azure-installation.md
// @greenlight-spec: 28-it-dashboard-ui.md

import * as React from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { MonoValue } from '@/components/greenlight/MonoValue';
import { StepHeader } from '@/components/install/StepHeader';
import { DraftNote, useApplyDraft, useInstallDraft } from '@/components/install/installDraft';
import { IdpCheckList } from '@/components/greenlight/idp/IdpCheckList';
import { IdpProviderFields, IdpProviderPicker } from '@/components/greenlight/idp/IdpProviderForm';
import {
  joinHostedDomains,
  splitHostedDomains,
  useIdpForm,
  type IdpFormInitial,
  type IdpFormState,
} from '@/components/greenlight/idp/useIdpForm';
import { api } from '@/lib/api';
import { errorBodyMessage, thrownMessage } from '@/lib/apiErrors';
import type { InstallationState, RoleSource } from '@greenlight/shared';
import type { IdpValidationResult, InstallDraft } from '@greenlight/contract';
import {
  getIdpProvider,
  setupStepHref,
  setupStepText,
  type IdpProviderId,
} from '@/lib/idpProviders';

/** Details persisted on the completed `idp` step (drives the resumable summary). */
interface IdpDetails {
  provider?: IdpProviderId;
  issuer?: string;
  client_id?: string;
  /** Generic OIDC only: the sign-in button label. */
  display_name?: string;
  /**
   * Where platform roles come from (docs/10 §IdP group → platform role
   * mapping). `idp` maps groups to roles; `greenlight` names an initial admin
   * by email and assigns roles in-product. Google is always `greenlight`.
   */
  role_source?: RoleSource;
  group_claim?: string;
  /**
   * Entra/Okta group→role mapping; the wizard's admin-group field writes
   * `org_admin`. Google carries none — its roles are managed in the dashboard.
   */
  role_groups?: { org_admin?: string[]; admin?: string[]; user?: string[] };
  /** Legacy single-field form written by earlier wizard versions; read-only. */
  admin_group_id?: string;
  /** Google only; empty means no Greenlight-side domain restriction. */
  hosted_domains?: string[];
  /** Greenlight-managed roles only: the org admin pre-created at save. */
  initial_admin_email?: string;
  /**
   * Generic OIDC under IdP-managed roles: the step completes only once a test
   * sign-in proves the provider emits the configured groups claim.
   */
  test_sign_in?: {
    status: 'required' | 'passed' | 'failed';
    code?: string;
    at?: string;
    passed_at?: string;
    email?: string;
  };
}

/** Loose email check for the Greenlight-managed initial-admin field. */
function looksLikeEmail(value: string): boolean {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value.trim());
}

/**
 * Whether a Google Workspace-domain list would refuse the administrator the
 * install is naming. An empty list restricts nothing and is a deliberate
 * delegation to the OAuth client's user type, so it warns about nothing.
 */
function hostedDomainsExcludeAdmin(hostedDomains: string, adminEmail: string): boolean {
  const domains = splitHostedDomains(hostedDomains);
  const adminDomain = adminEmail.trim().toLowerCase().split('@')[1];
  if (domains.length === 0 || !adminDomain) return false;
  return !domains.includes(adminDomain);
}

/** The saved initial admin, else the email the Organization step asked for. */
function prefilledAdminEmail(
  installationState: InstallationState,
  configured: IdpDetails | undefined,
): string {
  if (configured?.initial_admin_email) return configured.initial_admin_email;
  const installer = installationState.steps?.org_setup?.details?.['installer_email'];
  return typeof installer === 'string' ? installer : '';
}

/** The role source to prefill from a resumed config (Google is always greenlight). */
function configuredRoleSource(details: IdpDetails | undefined): RoleSource {
  if (details?.provider === 'google') return 'greenlight';
  if (details?.role_source) return details.role_source;
  // Back-compat with a pre-role_source persisted step: infer from the mapping.
  const rg = details?.role_groups;
  const hasMapping =
    (rg?.org_admin?.length ?? 0) > 0 ||
    (rg?.admin?.length ?? 0) > 0 ||
    (rg?.user?.length ?? 0) > 0 ||
    Boolean(details?.admin_group_id);
  return hasMapping ? 'idp' : 'greenlight';
}

/** The admin group shown on resume — `role_groups.org_admin`, or the legacy field. */
function configuredAdminGroup(details: IdpDetails | undefined): string {
  return details?.role_groups?.org_admin?.[0] ?? details?.admin_group_id ?? '';
}

interface IdpStepProps {
  /** Control-plane base URL; OIDC redirect URIs derive from it. */
  greenlightBaseUrl: string;
  installationState: InstallationState;
  blocked: boolean;
  /** Advance past the already-configured step without re-saving. */
  onContinue: () => void;
  /** Refetch install status and advance after a successful save. */
  onConfigured: () => Promise<void> | void;
  /** Refetch install status without advancing (a save that still awaits a test sign-in). */
  onRefreshStatus?: () => Promise<void> | void;
  /** A prefill draft to show for confirmation while no provider is configured. */
  draft?: InstallDraft['idp'];
  onDraftTouched?: () => void;
}

/** The form with every setter and selector marking the screen as edited first. */
function trackEdits(form: IdpFormState, markTouched: () => void): IdpFormState {
  const tracked: Record<string, unknown> = { ...form };
  for (const [key, value] of Object.entries(form)) {
    if (typeof value === 'function' && /^(set|select)/.test(key)) {
      tracked[key] = (...args: unknown[]) => {
        markTouched();
        return (value as (...a: unknown[]) => unknown)(...args);
      };
    }
  }
  return tracked as unknown as IdpFormState;
}

/** Fast client-side pre-check so an obviously malformed issuer fails without a
 * round trip; the server probe is the real validation. */
function isPlausibleIssuer(issuer: string): boolean {
  try {
    const url = new URL(issuer.trim());
    return url.protocol === 'https:' && url.hostname.includes('.');
  } catch {
    return false;
  }
}

export function IdpStep({
  greenlightBaseUrl,
  installationState,
  blocked,
  onContinue,
  onConfigured,
  onRefreshStatus,
  draft: incomingDraft,
  onDraftTouched,
}: IdpStepProps) {
  const configured = installationState.steps?.idp?.details as IdpDetails | undefined;
  const hasConfig = Boolean(configured?.provider);
  const { draft, markTouched } = useInstallDraft(
    hasConfig || blocked ? undefined : incomingDraft,
    onDraftTouched,
  );
  const draftProvider = draft ? getIdpProvider(draft.provider) : undefined;

  const [editing, setEditing] = React.useState(false);
  const adminEmailPrefill = prefilledAdminEmail(installationState, configured);
  const [initialAdminEmail, setInitialAdminEmail] = React.useState(adminEmailPrefill);

  const validateMutation = api.install.validateIdp.useMutation();
  const configureMutation = api.install.configureIdp.useMutation();

  const [checks, setChecks] = React.useState<IdpValidationResult | null>(null);
  const [validateError, setValidateError] = React.useState<string | null>(null);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  // Monotonic probe sequence: a response that lands after the issuer or
  // provider changed must not mark the *old* issuer as validated while the
  // form shows a new one.
  const probeSeq = React.useRef(0);

  const resetProbe = (): void => {
    probeSeq.current += 1;
    setChecks(null);
    setValidateError(null);
    setSaveError(null);
  };

  const initial: IdpFormInitial = {
    provider: configured?.provider ?? draft?.provider ?? null,
    issuerUrl: configured?.issuer ?? draft?.issuerUrl ?? '',
    clientId: configured?.client_id ?? draft?.clientId ?? '',
    displayName: configured?.display_name ?? '',
    roleSource: hasConfig
      ? configuredRoleSource(configured)
      : (draftProvider?.defaultRoleSource ?? (draft ? 'idp' : configuredRoleSource(undefined))),
    groupClaim: configured?.group_claim ?? draftProvider?.defaultGroupClaim ?? 'groups',
    adminGroupId: configuredAdminGroup(configured),
    itAdminGroupId: configured?.role_groups?.admin?.[0] ?? '',
    userGroupId: configured?.role_groups?.user?.[0] ?? '',
    hostedDomains: joinHostedDomains(configured?.hosted_domains),
    roleGroups: configured?.role_groups,
  };

  const rawForm = useIdpForm({
    initial,
    // A secret the draft carried is used only for the provider, issuer and
    // client ID it arrived with; the server holds the same rule.
    secretRequired: ({ sameProvider, sameIssuer, sameClientId }) =>
      !(draft?.clientSecretHeld && sameProvider && sameIssuer && sameClientId),
    reselectIsNoop: true,
    onInvalidateValidation: resetProbe,
  });
  useApplyDraft(draft, (d) => {
    rawForm.selectProvider(d.provider);
    rawForm.setIssuerUrl(d.issuerUrl);
    rawForm.setClientId(d.clientId);
  });
  const form = trackEdits(rawForm, markTouched);
  const usesHeldSecret = !form.secretRequired && form.clientSecret.trim() === '';

  const provider = form.providerDef;
  const base = greenlightBaseUrl.replace(/\/$/, '');
  const redirectUri = `${base}/auth/callback`;
  const signOutUri = `${base}/auth/login`;

  const handleValidate = async (): Promise<void> => {
    const seq = ++probeSeq.current;
    setChecks(null);
    setValidateError(null);
    setSaveError(null);

    if (!isPlausibleIssuer(form.issuerUrl)) {
      setValidateError('Confirm the issuer URL copied from your IdP is a valid https:// address.');
      return;
    }

    try {
      const res = await validateMutation.mutateAsync({
        body: {
          issuer_url: form.issuerUrl.trim(),
          ...(form.provider ? { provider: form.provider } : {}),
          client_id: form.clientId.trim(),
          ...(usesHeldSecret ? {} : { client_secret: form.clientSecret }),
        },
      });
      if (seq !== probeSeq.current) return; // stale response — form changed since
      if (res.status === 200) {
        // A failing check carries its own remediation on its row, so the alert
        // below the list only covers failures that produced no result at all.
        setChecks(res.body);
      } else {
        setValidateError(errorBodyMessage(res.body, 'Validation failed.'));
      }
    } catch (err) {
      if (seq !== probeSeq.current) return;
      setValidateError(
        thrownMessage(err, 'Could not reach the control plane. Check it is running and try again.'),
      );
    }
  };

  // The probe exercises the issuer, the client ID and the secret together, so
  // it needs all three — validating a half-filled form is not possible.
  const canValidate =
    form.issuerUrl.trim() !== '' &&
    form.clientId.trim() !== '' &&
    (form.clientSecret.trim() !== '' || usesHeldSecret);
  // `ok` is "nothing failed", not "everything passed": an inconclusive check —
  // the normal Google outcome — leaves the configuration saveable.
  const validated = checks?.ok === true;
  const canSave =
    validated &&
    form.fieldsComplete &&
    (form.effectiveRoleSource === 'greenlight' ? looksLikeEmail(initialAdminEmail) : true);

  const handleSave = async (): Promise<void> => {
    if (!form.provider) return;
    setSaveError(null);
    try {
      const body = {
        ...form.buildBody(),
        ...(form.effectiveRoleSource === 'greenlight' && initialAdminEmail.trim()
          ? { initial_admin_email: initialAdminEmail.trim() }
          : {}),
      };
      const res = await configureMutation.mutateAsync({ body });
      if (res.status === 200) {
        if (body.provider === 'oidc' && body.role_source === 'idp') {
          // The server holds the step pending until a test sign-in passes;
          // leave the form and show that requirement instead of advancing.
          setEditing(false);
          await onRefreshStatus?.();
        } else {
          await onConfigured();
        }
      } else {
        setSaveError(errorBodyMessage(res.body, 'Could not save the configuration.'));
      }
    } catch (err) {
      setSaveError(
        thrownMessage(
          err,
          'Could not save the configuration. Check the control plane and try again.',
        ),
      );
    }
  };

  const isSaving = configureMutation.isPending;

  const Header = (
    <StepHeader step="idp" title="Connect your identity provider">
      Greenlight uses your IdP for single sign-on. Configure one provider now; you can change it
      later under Settings → Identity.
    </StepHeader>
  );

  if (blocked) {
    return (
      <div className="space-y-6" data-testid="step-idp">
        {Header}
        <div
          className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground"
          data-testid="step-blocked"
        >
          This step is blocked until the verified-URL handoff is complete, because OIDC redirect
          URIs must use the final HTTPS control-plane URL.
        </div>
      </div>
    );
  }

  // Resumable summary: the step is already configured and we are not editing.
  if (hasConfig && !editing) {
    const configuredProvider = getIdpProvider(configured?.provider);
    const testSignIn = configured?.test_sign_in;
    const awaitingTestSignIn =
      configured?.provider === 'oidc' &&
      configuredRoleSource(configured) === 'idp' &&
      testSignIn !== undefined &&
      testSignIn.status !== 'passed';
    const groupClaim = configured?.group_claim ?? 'groups';
    return (
      <div className="space-y-6" data-testid="step-idp">
        {Header}
        <dl className="divide-y divide-border rounded-lg border text-sm" data-testid="idp-summary">
          <SummaryRow label="Provider" value={configuredProvider?.label ?? configured?.provider} />
          <SummaryRow label="Issuer" value={configured?.issuer} />
          <SummaryRow label="Client ID" value={configured?.client_id} />
          {configured?.provider === 'oidc' && (
            <SummaryRow label="Sign-in button" value={configured.display_name ?? 'SSO'} />
          )}
          <SummaryRow
            label="Role management"
            value={
              configuredRoleSource(configured) === 'idp' ? 'IdP-managed' : 'Greenlight-managed'
            }
          />
          {configured?.provider !== 'google' && configuredRoleSource(configured) === 'idp' && (
            <>
              <SummaryRow label="Group claim" value={configured?.group_claim} />
              <SummaryRow label="Org admin group" value={configuredAdminGroup(configured)} />
              <SummaryRow label="Admin group" value={configured?.role_groups?.admin?.[0]} />
              <SummaryRow label="User group" value={configured?.role_groups?.user?.[0]} />
            </>
          )}
          {configured?.provider === 'google' && (
            <SummaryRow
              label="Workspace domains"
              mono={Boolean(configured.hosted_domains?.length)}
              value={
                configured.hosted_domains?.length
                  ? configured.hosted_domains.join(', ')
                  : 'Any domain your OAuth client admits'
              }
            />
          )}
        </dl>
        {awaitingTestSignIn ? (
          <Alert
            variant={testSignIn.status === 'failed' ? 'destructive' : 'default'}
            data-testid="idp-test-signin-required"
          >
            <AlertTitle>Test sign-in required before you continue</AlertTitle>
            <AlertDescription className="space-y-3">
              <p>
                Greenlight cannot assume your provider emits a groups claim. Sign in once through
                the saved configuration; this step completes when the ID token carries the{' '}
                <span className="font-mono">{groupClaim}</span> claim.
              </p>
              {testSignIn.status === 'failed' && (
                <p data-testid="idp-test-signin-failed">
                  Your last test sign-in
                  {testSignIn.at ? ` at ${new Date(testSignIn.at).toLocaleString()}` : ''} was
                  refused: the ID token carried no <span className="font-mono">{groupClaim}</span>{' '}
                  claim. Check your provider's claim or scope mapping, or edit the configuration and
                  choose Greenlight-managed roles.
                </p>
              )}
              <div className="flex flex-col sm:flex-row gap-2">
                <Button asChild variant="outline" className="w-full sm:w-auto h-11 md:h-9">
                  <a
                    href={signOutUri}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="idp-test-login"
                  >
                    Test sign-in (opens your IdP)
                  </a>
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full sm:w-auto h-11 md:h-9"
                  onClick={() => void onRefreshStatus?.()}
                  data-testid="idp-test-signin-refresh"
                >
                  I've signed in — check again
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        ) : testSignIn?.status === 'passed' ? (
          <p className="text-sm text-muted-foreground" data-testid="idp-test-signin-passed">
            Test sign-in passed{testSignIn.email ? ` for ${testSignIn.email}` : ''}: the ID token
            carried the <span className="font-mono">{groupClaim}</span> claim.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            Optional: verify sign-in end to end against the saved configuration —{' '}
            <a
              href={signOutUri}
              target="_blank"
              rel="noreferrer"
              className="text-link underline"
              data-testid="idp-test-login"
            >
              test sign-in (opens your IdP)
            </a>
            .
          </p>
        )}
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            type="button"
            className="w-full sm:w-auto h-11 md:h-9"
            onClick={onContinue}
            disabled={awaitingTestSignIn}
            data-testid="step-next"
          >
            Continue
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full sm:w-auto h-11 md:h-9"
            onClick={() => {
              setEditing(true);
              resetProbe();
            }}
            data-testid="idp-reconfigure"
          >
            Reconfigure
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="step-idp">
      {Header}

      {draft && <DraftNote />}

      {(hasConfig || editing) && (
        <Alert data-testid="idp-replace-warning">
          <AlertTitle>Replacing the current provider</AlertTitle>
          <AlertDescription>
            Saving a new provider replaces the existing IdP and invalidates all active sessions.
            Everyone signs in again through the new provider.
          </AlertDescription>
        </Alert>
      )}

      {/* Provider selection — one decision per screen on narrow widths. A switch
          also resets the initial-admin email to its prefill. */}
      <IdpProviderPicker
        providerId={form.provider}
        legend="Choose a provider"
        onSelect={(id) => {
          if (id !== form.provider) setInitialAdminEmail(adminEmailPrefill);
          form.selectProvider(id);
        }}
      />

      {provider && (
        <div className="space-y-6" data-testid="idp-config-shell">
          {/* Redirect URIs to register in the IdP console. */}
          <div className="space-y-3">
            <h2 className="text-base font-semibold tracking-tight">
              Register these URIs in {provider.label}
            </h2>
            <dl className="divide-y divide-border rounded-lg border text-sm">
              <SummaryRow label="Redirect URI" value={redirectUri} />
              <SummaryRow label="Sign-out redirect" value={signOutUri} />
            </dl>
          </div>

          {/* Inline checklist + link to the per-provider guide. */}
          <div className="space-y-2">
            <h2 className="text-base font-semibold tracking-tight">Set up {provider.label}</h2>
            <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
              {provider.setupSteps(form.effectiveRoleSource).map((step) => {
                const text = setupStepText(step);
                const href = setupStepHref(step);
                return (
                  <li key={text}>
                    {href ? (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${text} (opens in new tab)`}
                        className="text-link underline"
                      >
                        {text}
                      </a>
                    ) : (
                      text
                    )}
                  </li>
                );
              })}
            </ol>
            <a
              href={provider.docsUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-block text-sm text-link underline"
              data-testid="idp-docs-link"
            >
              {provider.label} setup guide (screenshots)
            </a>
          </div>

          <IdpProviderFields
            form={form}
            redirectUri={redirectUri}
            secretKeptHint="A client secret filled in before the wizard opened is held for this provider, issuer and client ID. Leave blank to use it, or enter one to replace it."
            greenlightBlurb="Name an initial org admin; assign roles in the dashboard afterward."
            googleRolesNote="Google Workspace carries no group claim, so roles are managed in the dashboard. Name the first org admin below; assign the rest under Users after sign-in."
            hostedDomainsExtra={
              hostedDomainsExcludeAdmin(form.hostedDomains, initialAdminEmail) ? (
                <Alert data-testid="idp-hosted-domain-warning">
                  <AlertDescription>
                    None of these domains matches{' '}
                    <span className="font-mono">{initialAdminEmail.trim()}</span>, the administrator
                    you are naming below. Saved as-is, Google sign-in refuses that account and the
                    org has no usable administrator. Add their domain, or clear the list.
                  </AlertDescription>
                </Alert>
              ) : null
            }
            greenlightExtra={
              <div className="space-y-2">
                <Label htmlFor="idp-initial-admin">Initial org admin email</Label>
                <Input
                  id="idp-initial-admin"
                  type="email"
                  value={initialAdminEmail}
                  placeholder="it-admin@acme.com"
                  onChange={(e) => {
                    markTouched();
                    setInitialAdminEmail(e.target.value);
                  }}
                  className="h-11 md:h-9"
                  data-testid="idp-initial-admin-email"
                />
                <p className="text-xs text-muted-foreground">
                  Greenlight pre-creates this org admin; their first SSO sign-in (matched by email)
                  links the account. Double-check it — a typo leaves the org without an
                  administrator.
                </p>
              </div>
            }
          />

          {/* Validate: verify the issuer and the client registration together.
              The button needs a complete form because it now exercises all
              three fields, not the issuer alone. */}
          <div className="space-y-3">
            <Button
              type="button"
              variant="outline"
              className="w-full md:w-auto h-11 md:h-9"
              onClick={() => void handleValidate()}
              disabled={!canValidate || validateMutation.isPending}
              data-testid="idp-validate"
            >
              {validateMutation.isPending ? 'Validating…' : 'Validate configuration'}
            </Button>

            <IdpCheckList result={checks} running={validateMutation.isPending} />

            {validateError && (
              <Alert variant="destructive" data-testid="idp-validate-error">
                <AlertDescription>{validateError}</AlertDescription>
              </Alert>
            )}
          </div>

          {saveError && (
            <Alert variant="destructive" data-testid="idp-save-error">
              <AlertDescription>{saveError}</AlertDescription>
            </Alert>
          )}

          <Button
            type="button"
            className="w-full md:w-auto h-11 md:h-9"
            onClick={() => void handleSave()}
            disabled={!canSave || isSaving}
            data-testid="idp-save"
          >
            {isSaving ? 'Saving…' : 'Save & continue'}
          </Button>
        </div>
      )}
    </div>
  );
}

function SummaryRow({
  label,
  value,
  mono = true,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}) {
  if (!value) return null;
  return (
    <div className="flex flex-col sm:flex-row sm:items-center px-4 py-3 gap-1">
      <dt className="text-muted-foreground w-full sm:w-40 shrink-0">{label}</dt>
      <dd className="font-medium min-w-0">
        {mono ? (
          <MonoValue copyable wrap>
            {value}
          </MonoValue>
        ) : (
          <span className="text-muted-foreground font-normal">{value}</span>
        )}
      </dd>
    </div>
  );
}
